<template>
        <material-card :icon="icon" :color="color" :title="title" :text="subtitle">
            <template slot="add">
                <slot name="add"></slot>
            </template>
            <v-layout wrap align-start justify-start>
                <v-flex lg3>
                    <v-text-field v-if="!hideSearch" v-debounce:300ms="refresh" slot="actions-prepend"
                            v-model="search"
                            append-icon="search"
                            :label="$t('Core.Fields.search')"
                            single-line
                            hide-details
                            color="purple"
                    ></v-text-field>
                </v-flex>
                <slot name="filters"></slot>
            </v-layout>
            <v-data-table
                v-if="expandable"
                :no-data-text="$t('Common.no_data_text')"
                :hide-actions="hidePagination"
                :loading="loading"
                :headers="computedHeaders"
                :total-items="total"
                :items="items"
                :pagination.sync="pagination"
                :rows-per-page-items="perPageItems"
                v-model="selected"
                header-key="id"
                :select-all="selectAll"
                :item-key="itemKey">
                <template slot="items" slot-scope="props">
                    <slot name="items" :props="props"></slot>
                    <td class="text-xs-right">
                        <v-tooltip top>
                            <v-btn
                                slot="activator"
                                class="mx-0"
                                small
                                flat
                                icon
                                color="primary"
                                @click="props.expanded = !props.expanded">
                                <v-icon small>keyboard_arrow_down</v-icon>
                            </v-btn>
                            <span>{{$t('Core.Fields.more_info')}}</span>
                        </v-tooltip>
                    </td>
                </template>
                <template slot="expand" slot-scope="props">
                    <slot name="expand" :props="props"></slot>
                </template>
            </v-data-table>
            <v-data-table
                v-else
                :no-data-text="$t('Common.no_data_text')"
                :hide-actions="hidePagination"
                :loading="loading"
                :headers="computedHeaders"
                :total-items="total"
                :items="items"
                :pagination.sync="pagination"
                :rows-per-page-items="perPageItems"
                v-model="selected"
                header-key="id"
                :select-all="selectAll"
                :item-key="itemKey">
                <template slot="headerCell" slot-scope="{ header }">
                    <span class="subheading font-weight-light" :class="[headerColor]" v-text="header.text"/>
                </template>
                <template slot="items" slot-scope="{ item }">
                    <slot name="items" :item="item"></slot>
                    <td class="text-xs-right" v-show="!hideActions">
                        <slot name="action-items" :item="item"></slot>
                        <v-tooltip top v-if="!hideShowBtn">
                            <v-btn slot="activator" class="mx-0" small flat icon color="tertiary lighten-2" append :to="item.id + '/show'">
                                <v-icon  small>mdi-eye-settings</v-icon>
                            </v-btn>
                            <span>{{$t('Core.Fields.show')}}</span>
                        </v-tooltip>

                        <v-tooltip top v-if="!hideEditBtn">
                            <v-btn slot="activator" class="mx-0" small flat icon color="warning" append :to="item.id + '/update'">
                                <v-icon  small>edit</v-icon>
                            </v-btn>
                            <span>{{$t('Core.Fields.edit')}}</span>
                        </v-tooltip>

                        <v-tooltip top v-if="!hideDeleteBtn">
                            <v-btn slot="activator" class="mx-0" small flat icon color="error" @click="checkDelete(item)">
                                <v-icon  small>delete</v-icon>
                            </v-btn>
                            <span>{{$t('Core.Fields.delete')}}</span>
                        </v-tooltip>
                    </td>
                </template>
            </v-data-table>
            <v-dialog v-model="dialog" max-width="290">
                <v-card>
                    <v-card-title class="headline">{{$t('Pages.Delete.title')}}</v-card-title>

                    <v-card-text>
                        {{$t('Pages.Delete.subtitle')}}
                    </v-card-text>

                    <v-card-actions>
                        <v-spacer></v-spacer>

                        <v-btn
                                color="error darken-1"
                                @click="dialog = false"
                        >
                            {{$t('Common.no')}}
                        </v-btn>

                        <v-btn
                                color="info darken-1"
                                @click="deleteItem"
                        >
                            {{$t('Common.yes')}}
                        </v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>
        </material-card>
</template>

<script>
    export default {
        name: "eqt-table",
        props: {
            items: {
              type: Array,
              required: true
            },
            headers: {
                type: Array,
                required: true
            },
            icon: {
                type: String,
                default: ''
            },
            color: {
                type: String,
                default: 'tertiary'
            },
            headerColor: {
                type: String,
                default: 'text-primary text--darken-3'
            },
            itemKey: {
                type: String,
                default: 'id'
            },
            title: {
                type: String,
                default: ''
            },
            subtitle: {
                type: String,
                default: ''
            },
            total: {
                type: Number,
                default: 0,
            },
            loading: {
                type: Boolean,
                required: true
            },
            hideActions: {
                type: Boolean,
                default: false
            },
            hidePagination: {
                type: Boolean,
                default: false
            },
            hideSearch: {
                type: Boolean,
                default: false
            },
            hideShowBtn: {
                type: Boolean,
                default: false
            },
            hideEditBtn: {
                type: Boolean,
                default: false
            },
            hideDeleteBtn: {
                type: Boolean,
                default: false
            },
            selectAll: {
                type: Boolean,
                default: false
            },
            sortBy: {
                type: String,
                default: 'id'
            },
            sortDescending: {
                type: Boolean,
                default: true
            },
            expandable: {
                type: Boolean,
                default: false
            }
        },
        data: () => ({
            search: '',
            pagination: {},
            perPageItems: [10, 15, 25, 50, 100],
            dialog: false,
            selected: []
        }),
        computed:{
            computedHeaders(){
                if(!this.hideActions){
                    return this.headers.concat([{
                        sortable: false,
                        text: this.$t('Core.Fields.actions'),
                        value: '',
                        align: 'right'
                    }]);
                }
                return this.headers;
            }
        },
        created() {
        this.pagination = {descending: this.sortDescending, sortBy: this.sortBy};
        },
        methods: {

            refresh(){
                let data = {
                    q: this.search,
                    page: this.pagination.page,
                    pagination: this.pagination.rowsPerPage,
                    orderBy: this.pagination.sortBy ? (this.pagination.sortBy + "," + (this.pagination.descending ? 'desc' : 'asc')) : null
                }
                this.$emit('refresh', data);
            },
            checkDelete(item){
                this.selectedItem = item;
                this.dialog = true;
            },
            deleteItem(){
                this.$emit('delete', this.selectedItem);
                this.dialog = false;
            }
        },
        watch: {
            pagination(val){
                this.refresh();
            },
            selected(val){
                this.$emit('select', this.selected);
            }
/*            items(val){
                this.$nextTick(() => {
                    this.pagination.totalItems = this.total
                })
            }*/
        }
    }
</script>

<style scoped>

</style>
